import { useState } from "react";
import { CalendarDays, Filter, X, Check } from "lucide-react";
import { format, startOfMonth, endOfMonth, subMonths, startOfYear } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { ReportFilters as ReportFiltersType } from "@/lib/reports-engine";

interface ReportFiltersProps {
  filters: ReportFiltersType;
  onFiltersChange: (filters: ReportFiltersType) => void;
  clients?: Array<{ id: string; name: string }>;
  projects?: Array<{ id: string; name: string }>;
  showPaymentStatus?: boolean;
}

const categoryOptions = [
  { value: 'dev', label: 'Desenvolvimento' },
  { value: 'meeting', label: 'Reunião' },
  { value: 'research', label: 'Pesquisa' },
  { value: 'admin', label: 'Administrativo' },
];

const presetPeriods = [
  { id: 'this-month', label: 'Este mês', getValue: () => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) }) },
  { id: 'last-month', label: 'Mês anterior', getValue: () => ({ start: startOfMonth(subMonths(new Date(), 1)), end: endOfMonth(subMonths(new Date(), 1)) }) },
  { id: 'last-3-months', label: 'Últimos 3 meses', getValue: () => ({ start: startOfMonth(subMonths(new Date(), 2)), end: endOfMonth(new Date()) }) },
  { id: 'this-year', label: 'Este ano', getValue: () => ({ start: startOfYear(new Date()), end: endOfMonth(new Date()) }) },
];

export function ReportsFilterBar({
  filters,
  onFiltersChange,
  clients = [],
  projects = [],
  showPaymentStatus = false,
}: ReportFiltersProps) {
  const [showFilters, setShowFilters] = useState(false);
  
  const hasActiveFilters = 
    (filters.clientIds?.length || 0) > 0 || 
    (filters.projectIds?.length || 0) > 0 || 
    (filters.billingTypes?.length || 0) > 0 || 
    (filters.categories?.length || 0) > 0 ||
    (filters.paymentStatus?.length || 0) > 0;

  const activeFilterCount = [
    filters.clientIds?.length,
    filters.projectIds?.length,
    filters.billingTypes?.length,
    filters.categories?.length,
    filters.paymentStatus?.length,
  ].filter(Boolean).reduce((a, b) => (a || 0) + (b || 0), 0) || 0;

  const clearFilters = () => {
    onFiltersChange({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
      clientIds: undefined,
      projectIds: undefined,
      billingTypes: undefined,
      categories: undefined,
      paymentStatus: undefined,
    });
  };

  const applyPreset = (preset: typeof presetPeriods[0]) => {
    const { start, end } = preset.getValue();
    onFiltersChange({
      ...filters,
      startDate: start,
      endDate: end,
    });
  };

  const getActivePresetId = (): string | null => {
    for (const preset of presetPeriods) {
      const { start, end } = preset.getValue();
      if (
        format(filters.startDate, 'yyyy-MM-dd') === format(start, 'yyyy-MM-dd') &&
        format(filters.endDate, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd')
      ) {
        return preset.id;
      }
    }
    return null;
  };

  const activePresetId = getActivePresetId();

  return (
    <Card className="shadow-card border-0 bg-card/80 backdrop-blur-sm">
      <CardContent className="p-4">
        <div className="flex flex-col gap-4">
          {/* Period presets and date pickers */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Preset buttons */}
            <div className="flex flex-wrap gap-1.5">
              {presetPeriods.map((preset) => (
                <Button
                  key={preset.id}
                  variant={activePresetId === preset.id ? "default" : "outline"}
                  size="sm"
                  className={cn(
                    "h-8 text-xs px-3 transition-all duration-200",
                    activePresetId === preset.id && "shadow-sm"
                  )}
                  onClick={() => applyPreset(preset)}
                >
                  {activePresetId === preset.id && <Check className="h-3 w-3 mr-1" />}
                  {preset.label}
                </Button>
              ))}
            </div>

            <div className="h-6 w-px bg-border hidden sm:block" />

            {/* Custom date pickers */}
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={cn(
                      "gap-1.5 h-8 text-xs px-3",
                      !activePresetId && "border-primary/50"
                    )}
                  >
                    <CalendarDays className="h-3.5 w-3.5" />
                    {format(filters.startDate, "dd/MM/yyyy", { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.startDate}
                    onSelect={(d) => d && onFiltersChange({ ...filters, startDate: d })}
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
              <span className="text-muted-foreground text-xs">até</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={cn(
                      "gap-1.5 h-8 text-xs px-3",
                      !activePresetId && "border-primary/50"
                    )}
                  >
                    <CalendarDays className="h-3.5 w-3.5" />
                    {format(filters.endDate, "dd/MM/yyyy", { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.endDate}
                    onSelect={(d) => d && onFiltersChange({ ...filters, endDate: d })}
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Filter toggle */}
            <Button
              variant={showFilters || hasActiveFilters ? "secondary" : "ghost"}
              size="sm"
              className="h-8 text-xs ml-auto"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-3.5 w-3.5 mr-1.5" />
              Filtros
              {activeFilterCount > 0 && (
                <Badge variant="default" className="ml-1.5 h-4 w-4 p-0 text-[10px] rounded-full">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </div>

          {/* Expandable filters */}
          {showFilters && (
            <div className="pt-3 border-t space-y-3 animate-fade-in">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                <Select
                  value={filters.clientIds?.[0] || "all"}
                  onValueChange={(value) => onFiltersChange({ 
                    ...filters, 
                    clientIds: value && value !== "all" ? [value] : undefined 
                  })}
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os clientes</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.projectIds?.[0] || "all"}
                  onValueChange={(value) => onFiltersChange({ 
                    ...filters, 
                    projectIds: value && value !== "all" ? [value] : undefined 
                  })}
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Projeto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os projetos</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.billingTypes?.[0] || "all"}
                  onValueChange={(value) => onFiltersChange({ 
                    ...filters, 
                    billingTypes: value && value !== "all" ? [value as "hourly" | "fixed"] : undefined 
                  })}
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    <SelectItem value="hourly">Por Hora</SelectItem>
                    <SelectItem value="fixed">Preço Fixo</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={filters.categories?.[0] || "all"}
                  onValueChange={(value) => onFiltersChange({ 
                    ...filters, 
                    categories: value && value !== "all" ? [value] : undefined 
                  })}
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as categorias</SelectItem>
                    {categoryOptions.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {showPaymentStatus && (
                  <Select
                    value={filters.paymentStatus?.[0] || "all"}
                    onValueChange={(value) => onFiltersChange({ 
                      ...filters, 
                      paymentStatus: value && value !== "all" ? [value as "paid" | "pending" | "overdue"] : undefined 
                    })}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os status</SelectItem>
                      <SelectItem value="paid">Pago</SelectItem>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="overdue">Atrasado</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-8 text-xs text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3 mr-1" />
                  Limpar filtros
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
